import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import InfoIcon from '@mui/icons-material/Info';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { analyzeEmailContent } from '../services/openai';

interface EmailAnalyzerProps {
  html: string;
  isPro: boolean;
}

interface AnalysisResult {
  type: 'success' | 'warning' | 'error' | 'info';
  message: string;
  details?: string;
}

interface AnalysisCategory {
  name: string;
  icon: React.ReactNode;
  results: AnalysisResult[];
  score: number;
}

interface SpamRule {
  name: string;
  score: number;
  description: string;
  suggestion: string;
}

interface SpamAnalysis {
  totalScore: number;
  rules: SpamRule[];
}

const SPAM_RULES: SpamRule[] = [
  {
    name: "ALL_CAPS_SUBJECT",
    score: 2.0,
    description: "Subject line contains all capital letters",
    suggestion: "Use proper capitalization in subject lines"
  },
  {
    name: "EXCESSIVE_EXCLAMATION",
    score: 1.5,
    description: "Contains multiple exclamation marks",
    suggestion: "Limit exclamation marks to maintain professionalism"
  },
  {
    name: "SPAMMY_WORDS",
    score: 2.5,
    description: "Contains common spam trigger words",
    suggestion: "Avoid words like 'free', 'guarantee', 'no obligation'"
  },
  {
    name: "HTML_FONT_TINY",
    score: 1.0,
    description: "Contains very small font sizes",
    suggestion: "Use readable font sizes (minimum 11px)"
  },
  {
    name: "HIDDEN_TEXT",
    score: 3.0,
    description: "Contains text with same color as background",
    suggestion: "Ensure text contrasts with background"
  },
  {
    name: "IMAGE_ONLY",
    score: 1.5,
    description: "Email consists primarily of images",
    suggestion: "Balance images with text content"
  },
  {
    name: "CLICK_HERE",
    score: 1.0,
    description: "Uses generic call-to-action text",
    suggestion: "Use descriptive link text instead of 'click here'"
  },
  {
    name: "URGENT_SUBJECT",
    score: 2.0,
    description: "Subject line implies urgency",
    suggestion: "Avoid urgency unless truly time-sensitive"
  }
];

export const EmailAnalyzer: React.FC<EmailAnalyzerProps> = ({ html, isPro }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisCategory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [aiCategory, setAiCategory] = useState<AnalysisCategory | null>(null);

  const runBasicAnalysis = useCallback(async () => {
    if (!html) return;
    
    setIsAnalyzing(true);
    setError(null);

    try {
      // Run basic analysis in parallel
      const [spamResults, accessibilityResults, performanceResults] = await Promise.all([
        analyzeSpamScore(html),
        analyzeAccessibility(html),
        analyzePerformance(html)
      ]);

      const analysisCategories: AnalysisCategory[] = [
        {
          name: 'Accessibility',
          icon: <AccessibilityIcon />,
          results: accessibilityResults.results,
          score: accessibilityResults.score
        },
        {
          name: 'Spam Score',
          icon: <SecurityIcon />,
          results: spamResults.rules.map(rule => ({
            type: rule.score > 2 ? 'error' : 'warning',
            message: rule.description,
            details: rule.suggestion
          })),
          score: Math.max(0, 100 - (spamResults.totalScore * 10))
        },
        {
          name: 'Performance',
          icon: <SpeedIcon />,
          results: performanceResults.results,
          score: performanceResults.score
        }
      ];

      if (aiCategory) {
        analysisCategories.push(aiCategory);
      }

      setAnalysis(analysisCategories);
    } catch (err) {
      console.error('Analysis error:', err);
      setError('Failed to analyze email content. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  }, [html]);

  const runAiAnalysis = async () => {
    if (!html || !isPro) return;
    
    setIsAnalyzing(true);
    setError(null);

    try {
      const aiResults = await analyzeEmailContent(html);
      
      const newAiCategory: AnalysisCategory = {
        name: 'AI Analysis',
        icon: <SmartToyIcon />,
        results: [
          {
            type: 'info',
            message: `Content Tone: ${aiResults.tone}`,
            details: `Keywords: ${aiResults.keywords.join(', ')}`
          },
          {
            type: aiResults.readabilityScore > 80 ? 'success' : 'warning',
            message: `Readability Score: ${aiResults.readabilityScore}/100`,
            details: 'Based on content clarity and structure'
          },
          ...aiResults.suggestions.map(suggestion => ({
            type: 'info' as const,
            message: suggestion
          }))
        ],
        score: aiResults.readabilityScore
      };

      setAiCategory(newAiCategory);
      setAnalysis(prev => [...prev.filter(cat => cat.name !== 'AI Analysis'), newAiCategory]);
    } catch (err) {
      console.error('AI Analysis error:', err);
      setError('Failed to run AI analysis. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  useEffect(() => {
    runBasicAnalysis();
  }, [runBasicAnalysis]);

  const analyzeAccessibility = (html: string) => {
    const results: AnalysisResult[] = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Check for image alt text
    const images = doc.getElementsByTagName('img');
    if (images.length > 0) {
      const missingAlt = Array.from(images).filter(img => !img.hasAttribute('alt'));
      if (missingAlt.length > 0) {
        results.push({
          type: 'error',
          message: `${missingAlt.length} images missing alt text`,
          details: 'Add alt text to improve accessibility for screen readers'
        });
      } else {
        results.push({
          type: 'success',
          message: 'All images have alt text'
        });
      }
    }

    // Check for proper heading structure
    const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    if (headings.length > 0) {
      let previousLevel = 0;
      let hasError = false;
      Array.from(headings).forEach((heading) => {
        const level = parseInt(heading.tagName[1]);
        if (previousLevel > 0 && level - previousLevel > 1) {
          hasError = true;
        }
        previousLevel = level;
      });

      if (hasError) {
        results.push({
          type: 'warning',
          message: 'Improper heading structure',
          details: 'Ensure heading levels are properly nested'
        });
      } else {
        results.push({
          type: 'success',
          message: 'Proper heading structure'
        });
      }
    }

    // Calculate score based on results
    const score = results.reduce((acc, result) => {
      if (result.type === 'success') return acc + 1;
      if (result.type === 'warning') return acc + 0.5;
      return acc;
    }, 0) / results.length * 100;

    return { results, score };
  };

  const analyzeSpamScore = (html: string): SpamAnalysis => {
    const triggeredRules: SpamRule[] = [];
    let totalScore = 0;

    // Extract text content
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const textContent = doc.body.textContent || '';
    
    // Check for all caps
    if (textContent.toUpperCase() === textContent && textContent.length > 20) {
      triggeredRules.push(SPAM_RULES.find(rule => rule.name === "ALL_CAPS_SUBJECT")!);
    }

    // Check for excessive exclamation marks
    if ((textContent.match(/!/g) || []).length > 3) {
      triggeredRules.push(SPAM_RULES.find(rule => rule.name === "EXCESSIVE_EXCLAMATION")!);
    }

    // Check for spammy words
    const spamWords = ['free', 'guarantee', 'no obligation', 'winner', 'congratulations', 'urgent'];
    if (spamWords.some(word => textContent.toLowerCase().includes(word))) {
      triggeredRules.push(SPAM_RULES.find(rule => rule.name === "SPAMMY_WORDS")!);
    }

    // Check for tiny fonts
    if (html.match(/font-size:\s*([0-9]+)px/) && parseInt(html.match(/font-size:\s*([0-9]+)px/)![1]) < 11) {
      triggeredRules.push(SPAM_RULES.find(rule => rule.name === "HTML_FONT_TINY")!);
    }

    // Check for hidden text
    const styles = Array.from(doc.getElementsByTagName('style'));
    const hasHiddenText = styles.some(style => 
      style.textContent?.includes('color: #ffffff') || 
      style.textContent?.includes('color: white') ||
      style.textContent?.includes('display: none')
    );
    if (hasHiddenText) {
      triggeredRules.push(SPAM_RULES.find(rule => rule.name === "HIDDEN_TEXT")!);
    }

    // Check for image-heavy content
    const images = doc.getElementsByTagName('img').length;
    const textLength = textContent.length;
    if (images > 3 && textLength / images < 100) {
      triggeredRules.push(SPAM_RULES.find(rule => rule.name === "IMAGE_ONLY")!);
    }

    // Check for generic CTAs
    if (html.toLowerCase().includes('click here')) {
      triggeredRules.push(SPAM_RULES.find(rule => rule.name === "CLICK_HERE")!);
    }

    // Check for urgent language
    const urgentWords = ['urgent', 'immediate', 'act now', 'limited time'];
    if (urgentWords.some(word => textContent.toLowerCase().includes(word))) {
      triggeredRules.push(SPAM_RULES.find(rule => rule.name === "URGENT_SUBJECT")!);
    }

    // Calculate total score
    totalScore = triggeredRules.reduce((acc, rule) => acc + rule.score, 0);

    return {
      totalScore,
      rules: triggeredRules
    };
  };

  const analyzePerformance = (html: string) => {
    const results: AnalysisResult[] = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Check HTML size
    const size = new Blob([html]).size;
    if (size > 102400) { // 100KB
      results.push({
        type: 'warning',
        message: 'Large email size',
        details: `Size: ${(size / 1024).toFixed(1)}KB. Consider optimizing.`
      });
    } else {
      results.push({
        type: 'success',
        message: 'Good email size',
        details: `Size: ${(size / 1024).toFixed(1)}KB`
      });
    }

    // Check for inline styles
    const inlineStyles = doc.querySelectorAll('[style]').length;
    if (inlineStyles > 20) {
      results.push({
        type: 'warning',
        message: 'High number of inline styles',
        details: 'Consider consolidating styles'
      });
    } else {
      results.push({
        type: 'success',
        message: 'Acceptable use of inline styles'
      });
    }

    const score = results.reduce((acc, result) => {
      if (result.type === 'success') return acc + 1;
      if (result.type === 'warning') return acc + 0.5;
      return acc;
    }, 0) / results.length * 100;

    return { results, score };
  };

  if (!isPro) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Upgrade to Pro to access email analysis features
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Paper elevation={3} sx={{ p: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" color="text.primary">
            Email Analysis
          </Typography>
          {isPro && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SmartToyIcon />}
              onClick={runAiAnalysis}
              disabled={isAnalyzing}
            >
              Run AI Analysis
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isAnalyzing ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {analysis.map((category, index) => (
              <Accordion key={index} defaultExpanded={index === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {category.icon}
                    </ListItemIcon>
                    <Typography>{category.name}</Typography>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label={`${category.score}/100`}
                        color={category.score > 80 ? 'success' : category.score > 60 ? 'warning' : 'error'}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {category.results.map((result, rIndex) => (
                      <ListItem key={rIndex}>
                        <ListItemIcon>
                          {result.type === 'success' ? (
                            <CheckCircleIcon color="success" />
                          ) : result.type === 'warning' ? (
                            <WarningIcon color="warning" />
                          ) : result.type === 'error' ? (
                            <ErrorIcon color="error" />
                          ) : (
                            <InfoIcon color="info" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={result.message}
                          secondary={result.details}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
}; 